import React, { useEffect, useRef, useState, useCallback } from "react";
import clsx from "clsx";

import "./MobileBottomSheet.scss";

interface MobileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: string;
  className?: string;
}

export const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  title,
  className,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const currentYRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);

  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      // Reset state when opening
      setIsVisible(true);
      setIsAnimating(true);
      isDraggingRef.current = false;
      currentYRef.current = 0;

      // Trigger animation after visibility is set
      requestAnimationFrame(() => {
        if (overlayRef.current && sheetRef.current) {
          // Reset any previous styles
          overlayRef.current.style.opacity = "0";
          sheetRef.current.style.transform = "translateY(100%)";
          sheetRef.current.style.transition =
            "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";

          // Trigger the opening animation
          requestAnimationFrame(() => {
            if (overlayRef.current && sheetRef.current) {
              overlayRef.current.style.opacity = "1";
              sheetRef.current.style.transform = "translateY(0)";
            }
          });
        }

        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      });
    }
  }, [isOpen]);

  // Handle closing animation
  const handleClose = useCallback(() => {
    if (isAnimating) {
      return;
    }

    setIsAnimating(true);

    if (overlayRef.current && sheetRef.current) {
      overlayRef.current.style.opacity = "0";
      sheetRef.current.style.transform = "translateY(100%)";
    }

    setTimeout(() => {
      setIsVisible(false);
      setIsAnimating(false);
      onClose();
    }, 300);
  }, [isAnimating, onClose]);

  // Handle touch events for swipe to close - only on handle
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      return;
    }

    // Only allow drag from handle container
    const target = e.target as HTMLElement;
    const isHandleContainer = target.closest('.mobile-bottom-sheet-handle-container');
    
    if (!isHandleContainer) {
      return;
    }

    startYRef.current = e.touches[0].clientY;
    currentYRef.current = 0;
    isDraggingRef.current = true;

    if (sheetRef.current) {
      sheetRef.current.style.transition = "none";
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDraggingRef.current || e.touches.length !== 1) {
      return;
    }

    const deltaY = e.touches[0].clientY - startYRef.current;
    currentYRef.current = Math.max(0, deltaY); // Only allow downward drag

    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${currentYRef.current}px)`;
    }

    // Update overlay opacity based on drag distance
    if (overlayRef.current) {
      const opacity = Math.max(0.3, 1 - currentYRef.current / 200);
      overlayRef.current.style.opacity = opacity.toString();
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;

    if (sheetRef.current) {
      sheetRef.current.style.transition =
        "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)";
    }

    // Close if dragged down more than 100px
    if (currentYRef.current > 100) {
      handleClose();
    } else {
      // Snap back to original position
      if (sheetRef.current) {
        sheetRef.current.style.transform = "translateY(0)";
      }
      if (overlayRef.current) {
        overlayRef.current.style.opacity = "1";
      }
    }

    currentYRef.current = 0;
  }, [handleClose]);

  // Handle overlay click to close
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose],
  );

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when sheet is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={clsx(
        "mobile-bottom-sheet-portal",
        { visible: isVisible },
        className,
      )}
    >
      {/* Backdrop */}
      <div
        ref={overlayRef}
        className="mobile-bottom-sheet-overlay"
        onClick={handleOverlayClick}
      />

      {/* Sheet */}
      <div ref={sheetRef} className="mobile-bottom-sheet">
        <div className="mobile-bottom-sheet-content">
          {/* Drag handle container */}
          <div 
            className="mobile-bottom-sheet-handle-container"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClick={handleClose}
          >
            <div className="mobile-bottom-sheet-handle" />
          </div>
          
          {/* Title */}
          {title && (
            <div className="mobile-bottom-sheet-title">
              {title}
            </div>
          )}
          
          {/* Content */}
          <div className="mobile-bottom-sheet-body">{children}</div>
        </div>
      </div>
    </div>
  );
};
